import { useCallback, useEffect, useState } from 'react';
import { fetchDefaultDevice, rotateDefaultDevice } from '../api.js';
import { shareURL } from '../config.js';
import { useToast } from '../Toaster.jsx';

// Every account has one built-in display reached by a link rather than by
// pairing, for when typing a code into another device is more trouble than it
// is worth. It cannot be deleted, so rotating the link is how access is taken
// back.
export default function SharedDisplayPanel() {
  const notify = useToast();
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    fetchDefaultDevice()
      .then((data) => setLink(shareURL(data.token)))
      .catch((error) => notify(`Could not load the shared link: ${error.message}`, { tone: 'error' }));
  }, [notify]);

  useEffect(load, [load]);

  // Still here, and still correct, with nothing on the page calling it - the
  // button that did is commented out below. Left whole so bringing rotation
  // back is uncommenting one block rather than writing this again.
  async function handleRotate() {
    setBusy(true);
    try {
      const data = await rotateDefaultDevice();
      setLink(shareURL(data.token));
      setCopied(false);
      notify('New link created. The previous one no longer works.');
    } catch (error) {
      notify(`Could not rotate the link: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the input is selectable either way.
      notify('Could not copy - select the link and copy it manually.', { tone: 'error' });
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Shared display</h2>
        {/* Rotating the link is hidden for now, not removed. The route, the
            client call and handleRotate above are all still here and still
            work; this is the one control that reaches them, so commenting it
            out is what takes the feature off the page.

            Worth knowing before it comes back: one of these links is embedded
            live in the marketing site - VITE_DEMO_SHARE_CODE in
            frontend/www/.env.production - and rotating that account's leaves a
            dead television on the front page.
        <div className="panel-actions">
          <button type="button" onClick={handleRotate} disabled={busy}>
            {busy ? 'Working…' : 'New link'}
          </button>
        </div>
        */}
      </div>
      <p className="panel-hint">
        Open this link on any screen to show your shelf without pairing it.
        Anyone with the link can see it, so treat it like a password.
      </p>

      <div className="share-link">
        <input type="text" readOnly value={link} onFocus={(event) => event.target.select()} />
        <button type="button" onClick={handleCopy} disabled={!link}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        {link && (
          <a href={link} target="_blank" rel="noreferrer" className="share-open">
            Open
          </a>
        )}
      </div>
    </section>
  );
}
